<script setup lang="ts">
import { Dayjs } from 'dayjs'
import { computed, watch } from 'vue'
import { useStore } from 'vuex'

import { ChartUnit } from './types'
import VolumeGraphics from './VolumeGraphics.vue'

export interface AutomatableStaysStatsProps {
  earlyLimit: Dayjs
  lateLimit: Dayjs
  chartUnit: ChartUnit
  labelSets: string[]
}

const props = withDefaults(
  defineProps<AutomatableStaysStatsProps>(),
  {
    chartUnit: ChartUnit.MONTH,
    labelSets: () => []
  }
)

const store = useStore()

const stats = computed(() => store.state.stats.automatableStats)
const statsPercent = computed(() => {
  return stats.value.map((s) => {
    if (s.countStay <= 0) {
      return {
        prctValidatedStay: 0,
        prctValidatedSessionStay: 0,
        prctValidatedLongerStay: 0,
        prctValidatedAmbuStay: 0,
        prctValidatedOtherZeroDayStay: 0,
      }
    }

    return {
      prctValidatedStay: ((s.countAutomatedStay / s.countStay) * 100.0).toFixed(2),
      prctValidatedSessionStay: ((s.countAutomatedSessionStay / s.countStay) * 100.0).toFixed(2),
      prctValidatedLongerStay: ((s.countAutomatedLongerStay / s.countStay) * 100.0).toFixed(2),
      prctValidatedAmbuStay: ((s.countAutomatedAmbuStay / s.countStay) * 100.0).toFixed(2),
      prctValidatedOtherZeroDayStay: ((s.countAutomatedOtherZeroDayStay / s.countStay) * 100.0).toFixed(2),
    }
  })
})

watch([() => props.earlyLimit, () => props.lateLimit, () => props.chartUnit], () => {
  refresh()
}, { immediate: true })

function refresh() {
  store.dispatch('getAutomatableStaysStats', {
    earlyLimit: props.earlyLimit.format('YYYY-MM-DD'),
    lateLimit: props.lateLimit.format('YYYY-MM-DD'),
    unit: props.chartUnit,
  })
}


</script>

<template>
  <div class="stat">
    <h1 class="stat-title">
      Automatisation
    </h1>
    <volume-graphics
      :label-sets="labelSets"
      :stats="stats"
      :stats-percent="statsPercent"
      :prct-infos="{
        prctValidatedSessionStay: { label: 'Automatisés (séances)', unit: '%' },
        prctValidatedAmbuStay: { label: 'Automatisés (ambulatoire)', unit: '%' },
        prctValidatedOtherZeroDayStay: { label: 'Automatisés (0 jour hors ambulatoire et séances)', unit: '%' },
        prctValidatedLongerStay: { label: 'Automatisés (1-2 jours)', unit: '%' },
      }"
      :stats-infos="{
        countAutomatableStay: { label: 'Automatisables', unit: 'séjour(s)' },
        countAutomatedSessionStay: { label: 'Automatisés (séances)', unit: 'séjour(s)' },
        countAutomatedLongerStay: { label: 'Automatisés (1-2 jours)', unit: 'séjour(s)' },
        countAutomatedAmbuStay: { label: 'Automatisés (ambulatoire)', unit: 'séjour(s)' },
        countAutomatedOtherZeroDayStay: { label: 'Automatisés (0 jour hors ambulatoire et séances)', unit: 'séjour(s)' },
        countAutomatedStay: { label: 'Automatisés (total)', unit: 'séjour(s)' },
      }"
    />
  </div>
</template>
